/**
 * "Votre réglementation locale" card for the user dashboard.
 *
 * Surfaced as soon as the user has set their country at sign-up. Links
 * to `/regulations/{slug}` and shows the national authority + a short
 * compliance reminder. Hidden silently when:
 *   - the user has no country (legacy accounts created before the field
 *     was added);
 *   - the user picked "other" (outside our 18-country coverage).
 *
 * Kept as a stand-alone component so we can re-use it on the dashboard,
 * the mission-creation page and the future operator profile.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, ArrowRight, MapPin } from 'lucide-react';

// Map ISO code → { slug, flag, label, authority, authorityFull }. Kept
// in lockstep with /app/backend/email_service.py COUNTRY_REGULATION_SLUG.
const COUNTRY_DATA = {
  fr: { slug: 'france',      flag: '🇫🇷', label: 'France',      authority: 'DGAC',            authorityFull: 'Direction Générale de l\'Aviation Civile' },
  de: { slug: 'germany',     flag: '🇩🇪', label: 'Allemagne',   authority: 'DFS',             authorityFull: 'Deutsche Flugsicherung' },
  be: { slug: 'belgium',     flag: '🇧🇪', label: 'Belgique',    authority: 'Skeyes / DGTA',   authorityFull: 'Skeyes — DG Transport Aérien' },
  lu: { slug: 'luxembourg',  flag: '🇱🇺', label: 'Luxembourg',  authority: 'DAC Luxembourg',  authorityFull: 'Direction de l\'Aviation Civile' },
  nl: { slug: 'netherlands', flag: '🇳🇱', label: 'Pays-Bas',    authority: 'LVNL',            authorityFull: 'Luchtverkeersleiding Nederland' },
  ch: { slug: 'switzerland', flag: '🇨🇭', label: 'Suisse',      authority: 'BAZL / FOCA',     authorityFull: 'Office fédéral de l\'aviation civile' },
  at: { slug: 'austria',     flag: '🇦🇹', label: 'Autriche',    authority: 'Austro Control',  authorityFull: 'Austro Control GmbH' },
  cz: { slug: 'czechia',     flag: '🇨🇿', label: 'Tchéquie',    authority: 'AIM ČR / ÚCL',    authorityFull: 'Civil Aviation Authority of the Czech Republic' },
  sk: { slug: 'slovakia',    flag: '🇸🇰', label: 'Slovaquie',   authority: 'LPS SR',          authorityFull: 'Air Traffic Services of the Slovak Republic' },
  dk: { slug: 'denmark',     flag: '🇩🇰', label: 'Danemark',    authority: 'Trafikstyrelsen', authorityFull: 'Danish Civil Aviation and Railway Authority' },
  ie: { slug: 'ireland',     flag: '🇮🇪', label: 'Irlande',     authority: 'IAA',             authorityFull: 'Irish Aviation Authority' },
  ee: { slug: 'estonia',     flag: '🇪🇪', label: 'Estonie',     authority: 'Transpordiamet',  authorityFull: 'Estonian Transport Administration' },
  cy: { slug: 'cyprus',      flag: '🇨🇾', label: 'Chypre',      authority: 'DCA Cyprus',      authorityFull: 'Department of Civil Aviation — Cyprus' },
  is: { slug: 'iceland',     flag: '🇮🇸', label: 'Islande',     authority: 'Samgöngustofa',   authorityFull: 'Transport Authority of Iceland' },
  es: { slug: 'spain',       flag: '🇪🇸', label: 'Espagne',     authority: 'ENAIRE / AESA',   authorityFull: 'Agencia Estatal de Seguridad Aérea' },
  it: { slug: 'italy',       flag: '🇮🇹', label: 'Italie',      authority: 'ENAC / D-Flight', authorityFull: 'Ente Nazionale per l\'Aviazione Civile' },
  pl: { slug: 'poland',      flag: '🇵🇱', label: 'Pologne',     authority: 'PANSA / ULC',     authorityFull: 'Polish Air Navigation Services Agency' },
  pt: { slug: 'portugal',    flag: '🇵🇹', label: 'Portugal',    authority: 'ANAC',            authorityFull: 'Autoridade Nacional da Aviação Civil' },
};

export function LocalRegulationCard({ country }) {
  const { t } = useTranslation();
  const data = COUNTRY_DATA[country];
  if (!data) return null;

  return (
    <div
      className="bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-100 rounded-xl p-6 mb-8"
      data-testid="local-regulation-card"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-2xl shadow-sm flex-shrink-0">
          {data.flag}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-sky-600" />
            <span className="text-xs font-semibold uppercase tracking-wide text-sky-700">
              {t('dashboard.localReg.label', 'Votre réglementation locale')}
            </span>
          </div>
          <h3 className="text-lg font-bold text-slate-900 font-[Barlow]">
            {t('dashboard.localReg.title', 'Drone {{country}} — guide officiel', { country: data.label })}
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            {t(
              'dashboard.localReg.desc',
              "Toutes les zones officielles {{auth}} sont déjà chargées dans Drone Maps. Consultez les règles applicables avant de planifier votre prochaine mission.",
              { auth: data.authority },
            )}
          </p>
          <div className="flex items-center gap-3 mt-4">
            <Link
              to={`/regulations/${data.slug}`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-800"
              data-testid="local-regulation-link"
            >
              {t('dashboard.localReg.cta', 'Consulter le guide')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/missions/new"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              <MapPin className="w-3.5 h-3.5" />
              {t('dashboard.localReg.newMission', 'Nouvelle mission')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export const COUNTRY_DASHBOARD_DATA = COUNTRY_DATA;
